import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useFamilyChild } from './FamilyShell';
import { parentApi } from './parentApi';
import TimezoneSelect from '../../components/TimezoneSelect';

export default function FamilyAccount() {
  const { user, logout, updateUser } = useAuth();
  const { child } = useFamilyChild();
  const navigate = useNavigate();
  const contacts = useQuery(
    ['familyContacts', child.studentId],
    async () => (await parentApi.contacts(child.studentId)).data?.data,
    { enabled: !!child.studentId }
  );
  const requests = useQuery(
    ['familyRequests', child.studentId],
    async () => (await parentApi.requests(child.studentId)).data?.data,
    { enabled: !!child.studentId }
  );
  const [phone, setPhone] = useState(user?.phone || '');
  const [country, setCountry] = useState(user?.country || '');
  const [timezone, setTimezone] = useState(user?.timezone || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPhone(user?.phone || '');
    setCountry(user?.country || '');
    setTimezone(user?.timezone || '');
  }, [user]);

  const c = contacts.data || {};
  const reqs = requests.data || {};

  const saveProfile = async (e) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    setSaving(true);
    try {
      const res = await parentApi.updateMe({ phone, country, timezone });
      if (res.data?.success) {
        updateUser(res.data.data);
        setMsg('Saved.');
      }
    } catch (error) {
      setErr(error.response?.data?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    try {
      await parentApi.changePassword(currentPassword, newPassword);
      setMsg('Password updated.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      setErr(error.response?.data?.errors?.[0] || error.response?.data?.message || 'Could not update password.');
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>My account</Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography fontWeight={700}>{child.firstName}</Typography>
          <Typography color="text.secondary" variant="body2">{child.ageGroupLabel}</Typography>
        </CardContent>
      </Card>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Your people</Typography>
      {c.teacherName && (
        <Row title={`${c.teacherName} · Teacher`} meta="Class and homework" action="Message" onClick={() => navigate('/family/messages/new?channel=teacher')} />
      )}
      {c.rmName && (
        <Row title={`${c.rmName} · Relationship manager`} meta="Fees, schedule, complaints" action="Message" onClick={() => navigate('/family/messages/new?channel=rm')} />
      )}
      {c.salesName && (
        <Row title={`${c.salesName} · Joined you`} meta="Sales contact, for reference" />
      )}

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Your requests</Typography>
      <Row title="Leave" meta={`${reqs.leaveCount || 0} requested`} onClick={() => navigate('/family/leave')} />
      <Row
        title="Complaints"
        meta={reqs.openComplaints ? `${reqs.openComplaints} open` : 'None open'}
        onClick={() => navigate('/family/messages')}
      />

      {msg && <Alert severity="success" sx={{ my: 2 }}>{msg}</Alert>}
      {err && <Alert severity="error" sx={{ my: 2 }}>{err}</Alert>}

      <Typography variant="subtitle2" sx={{ mt: 3 }}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Email</Typography>
      <Typography sx={{ mb: 1 }}>{user?.email}</Typography>
      <Typography variant="caption" color="text.secondary">To change email, write to your coordinator.</Typography>

      <Box component="form" onSubmit={saveProfile} sx={{ mt: 2 }}>
        <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} margin="normal" />
        <TextField fullWidth label="Country" value={country} onChange={(e) => setCountry(e.target.value)} margin="normal" required />
        <TimezoneSelect
          label="Time zone"
          value={timezone}
          onChange={setTimezone}
          margin="normal"
          required
        />
        <Button type="submit" variant="contained" sx={{ mt: 1 }} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </Box>

      <Box component="form" onSubmit={savePassword} sx={{ mt: 4 }}>
        <Typography variant="subtitle1">Change password</Typography>
        <TextField fullWidth type="password" label="Current password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} margin="normal" />
        <TextField fullWidth type="password" label="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} margin="normal" />
        <Button type="submit" variant="outlined" sx={{ mt: 1 }}>Update password</Button>
      </Box>

      <Button color="secondary" sx={{ mt: 4 }} onClick={logout}>Sign out</Button>
    </Box>
  );
}

function Row({ title, meta, action, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        py: 1.25,
        borderBottom: '1px solid rgba(27,42,74,0.08)',
        display: 'flex',
        alignItems: 'center',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography fontWeight={600}>{title}</Typography>
        {meta ? <Typography variant="body2" color="text.secondary">{meta}</Typography> : null}
      </Box>
      {action ? <Typography variant="body2" color="primary">{action}</Typography> : onClick ? <Typography color="text.secondary">›</Typography> : null}
    </Box>
  );
}
